import { useEffect, useState } from "react";
import { api, type Booking, type BookingActivity, type BookingLineItem, type BookingStatus, type Client, type Package } from "../lib/api";
import { navigate } from "../lib/route";
import { formatPrice, formatDate } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";

interface Props {
  id: string;
}

const STATUS_LABELS: Record<BookingStatus, string> = {
  inquiry:    "Aanvraag",
  booked:     "Bevestigd",
  processing: "Verwerking",
  completed:  "Afgeleverd",
  cancelled:  "Geannuleerd",
};

const STATUS_COLORS: Record<BookingStatus, string> = {
  inquiry:    "#f59e0b",
  booked:     "#10b981",
  processing: "#6366f1",
  completed:  "#6b7280",
  cancelled:  "#ef4444",
};

function ACTIVITY_ICON(kind: BookingActivity["Kind"]): string {
  if (kind === "note") return "📝";
  if (kind === "status_change") return "🔄";
  return "🔔";
}

function formatActivityTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function activityBody(a: BookingActivity): string {
  if (a.Kind === "status_change") {
    // Replace status keys with Dutch labels
    return a.Body.replace(/\b(inquiry|booked|processing|completed|cancelled)\b/g, (match) => {
      return STATUS_LABELS[match as BookingStatus] ?? match;
    });
  }
  return a.Body;
}

export function BookingDetailPage({ id }: Props) {
  const [booking, setBooking]     = useState<Booking | null>(null);
  const [activities, setActivities] = useState<BookingActivity[]>([]);
  const [lineItems, setLineItems] = useState<BookingLineItem[]>([]);
  const [client, setClient]       = useState<Client | null>(null);
  const [_packages, setPackages]  = useState<Package[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // Note input
  const [noteText, setNoteText]   = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // Client notes
  const [clientNotes, setClientNotes] = useState("");
  const [savingClientNotes, setSavingClientNotes] = useState(false);

  // Cancel flow
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason]           = useState("");
  const [cancelling, setCancelling]               = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const bk = await api.getBooking(id);
      setBooking(bk);
      setClientNotes(bk.ClientNotes ?? "");

      const [acts, items, pkgs] = await Promise.all([
        api.listBookingActivities(id),
        api.getBookingLineItems(id),
        api.listPackages().catch(() => [] as Package[]),
      ]);
      setActivities(acts);
      setLineItems(items);
      setPackages(pkgs);

      if (bk.ClientID) {
        api.getClient(bk.ClientID).then(setClient).catch(() => {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden mislukt");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [id]);

  async function handleSaveClientNotes() {
    setSavingClientNotes(true);
    try {
      const updated = await api.updateBooking(id, { client_notes: clientNotes });
      setBooking(updated);
    } catch {
      // silently ignore
    } finally {
      setSavingClientNotes(false);
    }
  }

  async function handleAddNote() {
    const body = noteText.trim();
    if (!body) return;
    setAddingNote(true);
    try {
      const act = await api.addBookingNote(id, body);
      setActivities(prev => [...prev, act]);
      setNoteText("");
    } catch {
      // silently ignore
    } finally {
      setAddingNote(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      await api.transitionBooking(id, "cancelled");
      if (cancelReason.trim()) {
        await api.addBookingNote(id, cancelReason.trim());
      }
      setShowCancelConfirm(false);
      setCancelReason("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Annuleren mislukt");
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return (
    <div style={{ padding: "2rem", color: "var(--muted)" }}>Laden…</div>
  );

  if (error || !booking) {
    return (
      <div style={{ padding: "2rem" }}>
        <button className="btn-ghost" onClick={() => navigate("/bookings")} style={{ marginBottom: "1rem" }}>
          ← Boekingen
        </button>
        <div className="alert alert-error">{error ?? "Boeking niet gevonden"}</div>
      </div>
    );
  }

  const isCancelled  = booking.Status === "cancelled";
  const isCompleted  = booking.Status === "completed";
  const canCancel    = !isCancelled && !isCompleted;

  const lineItemsTotal = lineItems.reduce((s, li) => s + li.PriceCents * li.Quantity, 0);

  // Find cancellation banner info
  const cancellationActivity = isCancelled
    ? [...activities].reverse().find(a => a.Kind === "status_change" && a.Body.includes("cancelled"))
    : undefined;
  const cancellationNoteActivity = cancellationActivity
    ? activities.find(a => a.Kind === "note" && Math.abs(new Date(a.CreatedAt).getTime() - new Date(cancellationActivity.CreatedAt).getTime()) < 60_000)
    : undefined;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 860, margin: "0 auto" }}>

      {/* ── HEADER ROW ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button
          className="btn-ghost"
          style={{ fontSize: "0.875rem", padding: "0.35rem 0.75rem" }}
          onClick={() => navigate("/bookings")}
        >
          ← Boekingen
        </button>
        <h1 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 700, flex: 1 }}>{booking.Title}</h1>
        <StatusBadge
          label={STATUS_LABELS[booking.Status]}
          color={STATUS_COLORS[booking.Status]}
          style={{
            background: STATUS_COLORS[booking.Status] + "22",
            color: STATUS_COLORS[booking.Status],
            border: `1px solid ${STATUS_COLORS[booking.Status]}55`,
          }}
        />
      </div>

      {/* ── CANCELLATION BANNER ── */}
      {isCancelled && (
        <div style={{
          background: "color-mix(in srgb, #ef4444 10%, transparent)",
          border: "1px solid color-mix(in srgb, #ef4444 35%, transparent)",
          borderRadius: 8,
          padding: "0.75rem 1rem",
          marginBottom: "1.25rem",
          fontSize: "0.9rem",
        }}>
          <strong>Geannuleerd</strong>
          {cancellationNoteActivity && (
            <span style={{ marginLeft: "0.5rem", color: "var(--muted)" }}>— {cancellationNoteActivity.Body}</span>
          )}
        </div>
      )}

      {/* ── INFO GRID ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "1.25rem",
        }}>
          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Klant</div>
              {client ? (
                <button
                  className="btn-ghost"
                  style={{ padding: 0, fontWeight: 600, color: "var(--accent)", fontSize: "0.95rem" }}
                  onClick={() => navigate("/clients/" + booking.ClientID)}
                >
                  {client.DisplayName}
                </button>
              ) : (
                <span style={{ color: "var(--muted)", fontSize: "0.9rem" }}>—</span>
              )}
            </div>
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Datum & tijd</div>
              <div style={{ fontWeight: 500 }}>{booking.ScheduledAt ? formatDate(booking.ScheduledAt) : "—"}</div>
            </div>
            {booking.Location && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Locatie</div>
                <div>{booking.Location}</div>
              </div>
            )}
            {booking.DurationMin > 0 && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Duur</div>
                <div>{booking.DurationMin} min</div>
              </div>
            )}
          </div>
          {/* Right column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {booking.PackageName && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Pakket</div>
                <div style={{ fontWeight: 500 }}>{booking.PackageName}</div>
              </div>
            )}
            {booking.PriceCents > 0 && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Prijs</div>
                <div style={{ fontWeight: 600 }}>{formatPrice(booking.PriceCents, booking.Currency)}</div>
              </div>
            )}
            {booking.DepositCents > 0 && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Aanbetaling</div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span>{formatPrice(booking.DepositCents, booking.Currency)}</span>
                  {booking.DepositPaid
                    ? <span style={{ fontSize: "0.75rem", color: "#10b981", fontWeight: 600 }}>✓ Betaald</span>
                    : <span style={{ fontSize: "0.75rem", color: "#f59e0b" }}>Nog niet betaald</span>}
                </div>
              </div>
            )}
            {booking.DeliveryExpectedAt && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Verwachte levering</div>
                <div>{formatDate(booking.DeliveryExpectedAt)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── ORIGINAL INQUIRY ── */}
      {booking.Description && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Oorspronkelijke vraag
          </div>
          <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "var(--text)" }}>{booking.Description}</p>
        </div>
      )}

      {/* ── LINE ITEMS ── */}
      {lineItems.length > 0 && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Regelposten
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", color: "var(--muted)", fontSize: "0.8rem" }}>
                <th style={{ textAlign: "left", padding: "0.4rem 0", fontWeight: 600 }}>Omschrijving</th>
                <th style={{ textAlign: "right", padding: "0.4rem 0.5rem", fontWeight: 600 }}>Aantal</th>
                <th style={{ textAlign: "right", padding: "0.4rem 0.5rem", fontWeight: 600 }}>Stukprijs</th>
                <th style={{ textAlign: "right", padding: "0.4rem 0", fontWeight: 600 }}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map(li => (
                <tr key={li.ID} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "0.5rem 0" }}>{li.Name}</td>
                  <td style={{ textAlign: "right", padding: "0.5rem 0.5rem" }}>{li.Quantity}</td>
                  <td style={{ textAlign: "right", padding: "0.5rem 0.5rem" }}>{formatPrice(li.PriceCents, li.Currency)}</td>
                  <td style={{ textAlign: "right", padding: "0.5rem 0", fontWeight: 500 }}>
                    {formatPrice(li.PriceCents * li.Quantity, li.Currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ textAlign: "right", padding: "0.6rem 0.5rem 0", fontWeight: 700, fontSize: "0.95rem" }}>Subtotaal</td>
                <td style={{ textAlign: "right", padding: "0.6rem 0 0", fontWeight: 700, fontSize: "0.95rem" }}>
                  {formatPrice(lineItemsTotal, lineItems[0]?.Currency ?? "EUR")}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── NOTES ── */}
      {booking.Notes && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Notities
          </div>
          <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>{booking.Notes}</p>
        </div>
      )}

      {/* ── PREPARATION NOTES FOR CLIENT ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Preparation notes for client
        </div>
        <textarea
          rows={3}
          value={clientNotes}
          onChange={(e) => setClientNotes(e.target.value)}
          placeholder="Add preparation instructions…"
          style={{ width: "100%", fontSize: "0.9rem", resize: "vertical", boxSizing: "border-box", marginBottom: "0.5rem" }}
        />
        <small style={{ color: "var(--muted)", display: "block", marginBottom: "0.5rem" }}>
          Visible to the client in their portal under Sessions.
        </small>
        <button
          className="btn-primary"
          style={{ fontSize: "0.875rem", padding: "0.4rem 0.9rem" }}
          disabled={savingClientNotes}
          onClick={() => void handleSaveClientNotes()}
        >
          {savingClientNotes ? "Saving…" : "Save"}
        </button>
      </div>

      {/* ── ACTIVITY LOG ── */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Activiteiten
        </div>
        {activities.length === 0 ? (
          <p style={{ margin: "0 0 0.75rem", color: "var(--muted)", fontSize: "0.875rem" }}>Nog geen activiteit.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
            {[...activities].sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime()).map(a => (
              <div key={a.ID} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", fontSize: "0.875rem" }}>
                <span style={{ flexShrink: 0, fontSize: "1rem", marginTop: "0.05rem" }}>{ACTIVITY_ICON(a.Kind)}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ color: a.Kind === "status_change" ? "var(--accent)" : "var(--text)" }}>
                    {activityBody(a)}
                  </span>
                </div>
                <span style={{ color: "var(--muted)", fontSize: "0.78rem", flexShrink: 0, marginTop: "0.1rem" }}>
                  {formatActivityTime(a.CreatedAt)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Add note */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", marginBottom: "0.75rem" }}>
          <textarea
            placeholder="Notitie toevoegen..."
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={2}
            style={{ flex: 1, fontSize: "0.875rem", resize: "vertical" }}
            onKeyDown={e => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void handleAddNote();
              }
            }}
          />
          <button
            className="btn-primary"
            style={{ fontSize: "0.875rem", padding: "0.4rem 0.9rem", alignSelf: "flex-end" }}
            disabled={addingNote || !noteText.trim()}
            onClick={() => void handleAddNote()}
          >
            {addingNote ? "…" : "Toevoegen"}
          </button>
        </div>

        {/* Cancel booking */}
        {canCancel && !showCancelConfirm && (
          <button
            className="btn-ghost"
            style={{ fontSize: "0.875rem", color: "#ef4444", border: "1px solid #ef444444" }}
            onClick={() => setShowCancelConfirm(true)}
          >
            Annuleer boeking
          </button>
        )}
        {canCancel && showCancelConfirm && (
          <div style={{
            border: "1px solid color-mix(in srgb, #ef4444 35%, transparent)",
            borderRadius: 8,
            padding: "1rem",
            background: "color-mix(in srgb, #ef4444 6%, transparent)",
          }}>
            <div style={{ fontSize: "0.875rem", marginBottom: "0.5rem", fontWeight: 600 }}>
              Reden voor annulering (optioneel):
            </div>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={2}
              placeholder="Reden (optioneel)"
              style={{ width: "100%", fontSize: "0.875rem", marginBottom: "0.6rem", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className="btn-ghost"
                style={{ fontSize: "0.875rem", background: "#ef4444", color: "#fff", border: "none" }}
                disabled={cancelling}
                onClick={() => void handleCancel()}
              >
                {cancelling ? "Bezig…" : "Bevestig annulering"}
              </button>
              <button
                className="btn-ghost"
                style={{ fontSize: "0.875rem" }}
                disabled={cancelling}
                onClick={() => { setShowCancelConfirm(false); setCancelReason(""); }}
              >
                Terug
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
